import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';

import {
  PropertyRegistrationForm,
  FieldStaffItemWithId,
  ContractFactoryItemWithId,
  ReceiptDocumentItem,
} from '../types/property.types';
import { propertiesService, PropertyApiResponse } from '../services/properties.service';
import { mapApiResponseToForm } from '../mappers/property-response-mapper';
import { mapFormToUpdatePropertyRequest } from '../mappers/update-property-mapper';
import { validatePropertyRegistrationForm } from '../validations/create-properties-validation';
import { useDepartments } from '../../companies/hooks/use-departments';
import { useStaffs } from '../../companies/hooks/use-staffs';
import { companiesService } from '../../companies/services/companies.service';

import { useLoadingProgress } from '@/hooks/use-loading-progress';
import { FunctionName, PermissionAction, RoleKey } from '@/lib/enums';
import { paths } from '@/config/paths';
import { Department, Staff } from '@/features/companies/types';

export const usePropertyDetailForm = () => {
  const { propertyId } = useParams<{ propertyId: string }>();
  const [error, setError] = useState<string | null>(null);
  const { isLoading, setIsLoading } = useLoadingProgress();
  const [originalFormData, setOriginalFormData] = useState<PropertyRegistrationForm | null>(null);
  const [formData, setFormData] = useState<PropertyRegistrationForm>({
    property_name: '',
    property_name_abbreviation: '',
    address: '',
    start_date: '',
    customer_id: '',
    construction_company_id: '',
    department_code: 0,
    sales_representative_code: '',
    delivery_person_code: '',
    field_staffs: [],
    contract_factories: [],
  });
  const [detailFormData, setDetailFormData] = useState<PropertyApiResponse | null>(null);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Companies state
  const [customers, setCustomers] = useState<unknown[]>([]);
  const [constructionCompanies, setConstructionCompanies] = useState<unknown[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [canUpdateProperty, setCanUpdateProperty] = useState<boolean>(false);
  const [canManagePrice, setCanManagePrice] = useState<boolean>(false);
  const [salesStaffSelected, setSalesStaffSelected] = useState<Staff | null>(null);
  const [deliveryStaffSelected, setDeliveryStaffSelected] = useState<Staff | null>(null);
  const [departmentSelected, setDepartmentSelected] = useState<Department | null>(null);
  const [hasDocumentsUploaded, setHasDocumentsUploaded] = useState<boolean>(false);
  const navigate = useNavigate();

  // Refs to track if data has been loaded (prevent duplicate calls in StrictMode)
  const companiesLoadedRef = useRef(false);
  const propertyLoadedRef = useRef<string | null>(null);
  const propertyDataLoadedRef = useRef(false); // Track if property data has been set to state

  // Hooks for departments and staffs
  const { departments, loading: departmentsLoading } = useDepartments(PermissionAction.UPDATE);
  const { loading: staffsLoading, loadStaffsByDepartment, getStaffsByDepartment } = useStaffs();

  // Use the comprehensive validation system
  const validation = useMemo(() => {
    return validatePropertyRegistrationForm(formData, touchedFields);
  }, [formData, touchedFields]);

  // Get validation errors from validation result
  const validationErrors = validation.errors;

  // Load customers and construction companies
  useEffect(() => {
    // Skip if already loaded
    if (companiesLoadedRef.current) {
      // Make sure loading is false when skipping
      setCompaniesLoading(false);
      return;
    }

    companiesLoadedRef.current = true; // Mark as loaded immediately
    let isMounted = true;

    const loadCompanies = async () => {
      try {
        setCompaniesLoading(true);

        // Load both in parallel for better performance
        const [customersResponse, constructionCompaniesResponse] = await Promise.all([
          companiesService.getCompaniesByPartnerClassification(1, FunctionName.PROPERTY_MANAGEMENT),
          companiesService.getCompaniesByPartnerClassification(3, FunctionName.PROPERTY_MANAGEMENT),
        ]);

        const customersData = Array.isArray(customersResponse.data.data)
          ? customersResponse.data.data
          : [];

        const constructionCompaniesData = Array.isArray(constructionCompaniesResponse.data.data)
          ? constructionCompaniesResponse.data.data
          : [];

        // Set data regardless of mount status to handle StrictMode double-mounting
        setCustomers(customersData);
        setConstructionCompanies(constructionCompaniesData);
      } catch (err) {
        if (isMounted) {
          console.error('Error loading companies:', err);
          companiesLoadedRef.current = false; // Reset on error to allow retry
        }
      } finally {
        // Always set loading to false, regardless of mount status
        // This prevents the loading state from being stuck
        setCompaniesLoading(false);
      }
    };

    void loadCompanies();

    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency - only load once on mount

  // Load property data
  useEffect(() => {
    // Skip API call if already loading/loaded this property
    if (propertyLoadedRef.current === propertyId) {
      // But still set loading to false if data has been loaded
      if (propertyDataLoadedRef.current) {
        setIsLoading(false);
      }
      return;
    }

    let isMounted = true;

    const loadPropertyData = async () => {
      if (!propertyId) {
        setError('Property ID is required');
        setIsLoading(false);
        return;
      }

      try {
        propertyLoadedRef.current = propertyId; // Mark this property as loading
        propertyDataLoadedRef.current = false; // Reset data loaded flag
        setIsLoading(true);
        setError(null);

        // Fetch property data from API
        const response = await propertiesService.getPropertyById(propertyId);

        const apiData = response.data.data; // Extract actual data from ApiResponse wrapper
        setDetailFormData(apiData);

        let convertedData;
        try {
          convertedData = mapApiResponseToForm(apiData);
        } catch (convertError) {
          console.error('Error converting API data:', convertError);
          throw convertError;
        }

        // Set data regardless of mount status to handle StrictMode double-mounting
        // The ref prevents duplicate API calls, so data will always be from the correct request
        setFormData(convertedData);
        setOriginalFormData(convertedData);
        propertyDataLoadedRef.current = true; // Mark that data has been loaded to state
        setCanUpdateProperty(apiData.isCanUpdate ?? false);
        setCanManagePrice(apiData.isCanManagePrice ?? false);
        setSalesStaffSelected(apiData.saleStaff);
        setDeliveryStaffSelected(apiData.deliveryStaff);
        setDepartmentSelected(apiData.department);
        // Only set loading to false after data is successfully set
        // This ensures the form doesn't show validation errors before data is populated
        setIsLoading(false);
      } catch (err) {
        if (isMounted) {
          console.error('Error loading property data:', err);
          setError('現場データの読み込みに失敗しました。');
          propertyLoadedRef.current = null; // Reset on error to allow retry
          propertyDataLoadedRef.current = false;
        }
        navigate(paths.notFound.path);

        // Set loading to false on error too
        setIsLoading(false);
      }
    };

    void loadPropertyData();

    return () => {
      isMounted = false;
    };
  }, [propertyId]); // Only depends on propertyId

  // Check if form has been modified
  const hasFormChanged = useCallback(() => {
    if (!originalFormData) return false;
    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  }, [formData, originalFormData]);

  // Update field
  const updateField = useCallback(
    (field: keyof PropertyRegistrationForm, value: string | number | boolean | undefined) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      // For postal code fields, handle touched state based on values
      if (field === 'postal_code_first_half' || field === 'postal_code_latter_half') {
        const firstHalf =
          field === 'postal_code_first_half' ? value : formData.postal_code_first_half;
        const latterHalf =
          field === 'postal_code_latter_half' ? value : formData.postal_code_latter_half;

        // If both fields are empty, remove them from touched state
        if (
          (!firstHalf || firstHalf === 0 || firstHalf === '') &&
          (!latterHalf || latterHalf === 0 || latterHalf === '')
        ) {
          setTouchedFields((prev) => {
            const newTouched = new Set(prev);
            newTouched.delete('postal_code_first_half');
            newTouched.delete('postal_code_latter_half');
            return newTouched;
          });
        } else {
          // If at least one field has value, mark both as touched
          setTouchedFields((prev) =>
            new Set(prev).add('postal_code_first_half').add('postal_code_latter_half')
          );
        }
      } else {
        // For other fields, mark as touched
        setTouchedFields((prev) => new Set(prev).add(field));
      }

      // Note: Validation errors are automatically updated via the validation memo
      // No need to manually clear errors here
    },
    [formData.postal_code_first_half, formData.postal_code_latter_half]
  );

  // Add field staff member
  const addFieldStaff = useCallback(() => {
    const newStaff: FieldStaffItemWithId = {
      id: `temp_staff_${Date.now()}`, // Temporary id for React key only, not sent to backend
      name: '',
      tel: '',
      email: '',
    };
    setFormData((prev) => ({
      ...prev,
      field_staffs: [...(prev.field_staffs as FieldStaffItemWithId[]), newStaff],
    }));
  }, []);

  // Update field staff
  const updateFieldStaff = useCallback(
    (id: string, field: keyof FieldStaffItemWithId, value: string | number) => {
      setFormData((prev) => ({
        ...prev,
        field_staffs: (prev.field_staffs as FieldStaffItemWithId[]).map((staff) =>
          staff.id === id ? { ...staff, [field]: value } : staff
        ),
      }));
      setTouchedFields((prev) => new Set(prev).add(`field_staffs.${id}.${field}`));
    },
    []
  );

  // Remove field staff
  const removeFieldStaff = useCallback((id: string) => {
    setFormData((prev) => ({
      ...prev,
      field_staffs: (prev.field_staffs as FieldStaffItemWithId[]).filter(
        (staff) => staff.id !== id
      ),
    }));
  }, []);

  // Add contract factory
  const addContractFactory = useCallback(() => {
    const newFactory: ContractFactoryItemWithId = {
      id: `temp_factory_${Date.now()}`, // Temporary id for React key only, not sent to backend
      factory: '',
      delivery_classification: '',
      quantity: 0,
    };
    setFormData((prev) => ({
      ...prev,
      contract_factories: [...(prev.contract_factories as ContractFactoryItemWithId[]), newFactory],
    }));
  }, []);

  // Update contract factory
  const updateContractFactory = useCallback(
    (id: string, field: keyof ContractFactoryItemWithId, value: string | number) => {
      setFormData((prev) => ({
        ...prev,
        contract_factories: (prev.contract_factories as ContractFactoryItemWithId[]).map(
          (factory) => (factory.id === id ? { ...factory, [field]: value } : factory)
        ),
      }));
      setTouchedFields((prev) => new Set(prev).add(`contract_factories.${id}.${field}`));
    },
    []
  );

  const updateReceiptDocument = useCallback((receiptDocuments: ReceiptDocumentItem[]) => {
    setFormData((prev) => ({
      ...prev,
      receipt_documents: receiptDocuments,
    }));
    setTouchedFields((prev) => new Set(prev).add(`receipt_documents`));
  }, []);

  // Remove contract factory
  const removeContractFactory = useCallback((id: string) => {
    setFormData((prev) => ({
      ...prev,
      contract_factories: (prev.contract_factories as ContractFactoryItemWithId[]).filter(
        (factory) => factory.id !== id
      ),
    }));
  }, []);

  // Staff management - handle department change
  const handleDepartmentChange = useCallback(
    (departmentId: string) => {
      // Always load both sales and delivery staffs for the department
      // The loadStaffsByDepartment function already has internal caching logic
      void loadStaffsByDepartment(RoleKey.SALE, departmentId, FunctionName.PROPERTY_MANAGEMENT);
      void loadStaffsByDepartment(
        RoleKey.DELIVERY_AND_OUTSOURCING_DELIVERY,
        departmentId,
        FunctionName.PROPERTY_MANAGEMENT
      );
    },
    [loadStaffsByDepartment]
  );

  // Helper function to convert department code to ID
  const findDepartmentIdByCode = (code: number): string => {
    // Handle both string and number comparison for type safety
    const department = departments.find((dept) => {
      return (
        dept.code === code ||
        dept.code?.toString() === code.toString() ||
        Number(dept.code) === code
      );
    });

    return department?.id ?? '';
  };

  // Get staffs for current department
  const departmentId = formData.department_code
    ? findDepartmentIdByCode(formData.department_code)
    : '';
  const salesStaffs = departmentId ? getStaffsByDepartment(departmentId, RoleKey.SALE) : [];
  const deliveryStaffs = departmentId
    ? getStaffsByDepartment(departmentId, RoleKey.DELIVERY_AND_OUTSOURCING_DELIVERY)
    : [];

  // Validate field
  const validateField = useCallback(
    (field: string) => {
      const validation = validatePropertyRegistrationForm(formData, touchedFields);
      const fieldError = validation.errors.find((error) => error.field === field);
      return fieldError ? fieldError.message : '';
    },
    [formData, touchedFields]
  );

  // Check if field is valid
  const isFieldValid = useCallback(
    (field: string) => {
      const validation = validatePropertyRegistrationForm(formData, touchedFields);
      return !validation.errors.some((error) => error.field === field);
    },
    [formData, touchedFields]
  );

  // Get field error
  const getFieldError = useCallback(
    (field: string) => {
      return validateField(field);
    },
    [validateField]
  );

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Mark all required fields as touched to show all validation errors
      const requiredFields = [
        'property_name',
        'property_name_abbreviation',
        'address',
        'start_date',
        'customer_id',
        'construction_company_id',
        'construction_period_from', // Add period dates to required fields for validation display
        'construction_period_to',
        'department_code',
        'sales_representative_code',
        'delivery_person_code',
      ];
      const allTouchedFields = new Set([...touchedFields, ...requiredFields]);
      setTouchedFields(allTouchedFields);

      // Validate form with all fields marked as touched
      const validation = validatePropertyRegistrationForm(formData, allTouchedFields);
      if (!propertyId || !validation.isValid) {
        setIsLoading(false);
        return false;
      }

      // Check if form has changes or documents have been uploaded
      if (!hasFormChanged() && !hasDocumentsUploaded) {
        setIsLoading(false);
        return false;
      }

      await propertiesService.updateProperty(
        propertyId,
        mapFormToUpdatePropertyRequest(formData, canManagePrice)
      );

      return true;
    } catch (err) {
      setError('更新に失敗しました。もう一度お試しください。');
      console.error('Error updating property:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [formData, touchedFields, hasFormChanged, originalFormData, propertyId, hasDocumentsUploaded]);

  // Reset form to original data
  const resetForm = useCallback(() => {
    if (originalFormData) {
      setFormData(originalFormData);
      setTouchedFields(new Set());
      setHasDocumentsUploaded(false);
    }
  }, [originalFormData]);

  // Callback to notify when documents are uploaded
  const onDocumentsUploaded = useCallback(() => {
    setHasDocumentsUploaded(true);
  }, []);

  // Check if submit is disabled
  // Submit button should only be enabled when:
  // 1. Form is not currently submitting
  // 2. Form has changes compared to original data OR documents have been uploaded
  // 3. All validation passes (no errors)
  const isSubmitDisabled = useMemo(() => {
    const disabled =
      isLoading || (!hasFormChanged() && !hasDocumentsUploaded) || !validation.isValid;

    return disabled;
  }, [
    isLoading,
    hasFormChanged,
    hasDocumentsUploaded,
    validation.isValid,
    validation.errors.length,
  ]);

  // Load staffs when formData.department_code is available (on initial load)
  useEffect(() => {
    if (formData.department_code && departmentId) {
      void loadStaffsByDepartment(RoleKey.SALE, departmentId, FunctionName.PROPERTY_MANAGEMENT);
      void loadStaffsByDepartment(
        RoleKey.DELIVERY_AND_OUTSOURCING_DELIVERY,
        departmentId,
        FunctionName.PROPERTY_MANAGEMENT
      );
    }
  }, [formData.department_code, departmentId, loadStaffsByDepartment]);

  return {
    // Data
    propertyId: propertyId,
    formData,
    originalFormData,
    detailFormData,
    // State
    isLoading,
    error,
    hasFormChanged: hasFormChanged(),

    // Form actions
    updateField,
    addFieldStaff,
    updateFieldStaff,
    updateReceiptDocument,
    removeFieldStaff,
    addContractFactory,
    updateContractFactory,
    removeContractFactory,
    handleSubmit,
    resetForm,
    handleDepartmentChange,

    // Validation
    getFieldError,
    isFieldValid,
    validationErrors,

    // Departments and Staffs
    departments,
    departmentSelected,
    departmentsLoading,
    salesStaffs,
    salesStaffSelected,
    deliveryStaffs,
    deliveryStaffSelected,
    staffsLoading,

    // Companies (for autocomplete)
    customers,
    constructionCompanies,
    companiesLoading,

    // Computed properties
    isSubmitDisabled,
    canUpdateProperty,
    canManagePrice,

    // Document upload tracking
    onDocumentsUploaded,
  };
};
